import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import {
  subscribeAuth, waitForAuth, signOut,
} from "../services/auth";
import {
  normalizeCandidate, usernameValidationError,
} from "../utils/username";
import {
  subscribeMyIncomingPending, respondContact,
} from "../services/contactRequests";

// Profile edit surface for an already-signed-up user. Same field set
// as /role-pick — display name, username, role, WhatsApp — with the
// same atomic username-rename semantics so switching your @handle
// doesn't leave the Usernames collection in a half-state.

const COUNTRIES = [
  { code: "+91", label: "🇮🇳" }, { code: "+1",  label: "🇺🇸" },
  { code: "+44", label: "🇬🇧" }, { code: "+61", label: "🇦🇺" },
  { code: "+65", label: "🇸🇬" }, { code: "+971",label: "🇦🇪" },
  { code: "+49", label: "🇩🇪" }, { code: "+33", label: "🇫🇷" },
  { code: "+81", label: "🇯🇵" }, { code: "+82", label: "🇰🇷" },
  { code: "+55", label: "🇧🇷" },
];

const getMyProfile = httpsCallable(functions, "popMarketGetMyProfile");
const updateMyProfile = httpsCallable(functions, "popMarketUpdateMyProfile");

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
function normalizePhone(local, cc) {
  const digits = String(local || "").replace(/\D+/g, "");
  if (!digits) return "";
  const ccDigits = cc.replace(/\D/g, "");
  const bare = digits.startsWith(ccDigits) ? digits.slice(ccDigits.length) : digits;
  return cc + bare;
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState(null);
  const [cc, setCc] = useState("+91");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [stage, setStage] = useState("init");
  // Tracks fields the user has actively typed into since mount. The
  // async server-hydration must not stomp those — otherwise typing
  // in the gap between mount and callable-return silently loses.
  const editedRef = useRef({
    displayName: false, username: false, role: false, phone: false,
  });
  const [pendingRequests, setPendingRequests] = useState([]);
  useEffect(() => {
    if (!user?.uid) return;
    return subscribeMyIncomingPending(user.uid, setPendingRequests);
  }, [user?.uid]);

  useEffect(() => subscribeAuth(setUser), []);

  useEffect(() => {
    (async () => {
      setStage("checking-auth");
      let u = auth.currentUser;
      if (!u) {
        setStage("waiting-auth");
        u = await Promise.race([
          waitForAuth(),
          new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
        ]);
      }
      if (!u) { navigate("/signin", { replace: true }); return; }
      setUser(u);
      setDisplayName(u.displayName || "");
      // Render the form immediately with auth-only defaults so the
      // user can save even if the profile fetch never returns.
      setAppUser({});
      setStage("form-ready");

      // Load current profile from the server-side callable — bypasses
      // the client Firestore SDK's flaky state that was hanging reads
      // on certain accounts. When it returns, overwrite the auth-based
      // pre-fills unconditionally so the freshest server data wins
      // (auth.currentUser.displayName never changes on rename, so it
      // otherwise masks recently-saved edits).
      try {
        const res = await getMyProfile();
        const doc = res?.data?.appUser;
        if (!doc) return;
        setAppUser(doc);
        // Only hydrate fields the user hasn't already typed into —
        // otherwise a slow callable stomps their in-progress edits.
        if (doc.displayName && !editedRef.current.displayName) {
          setDisplayName(doc.displayName);
        }
        if (doc.username && !editedRef.current.username) {
          setUsername(doc.username);
        }
        if (doc.popMarketRole && !editedRef.current.role) {
          setRole(doc.popMarketRole);
        }
        const wa = doc?.popMarketContact?.whatsapp;
        if (wa && !editedRef.current.phone) {
          const match = COUNTRIES.find((c) => wa.startsWith(c.code));
          if (match) {
            setCc(match.code);
            setPhone(wa.slice(match.code.length));
          }
        }
      } catch (_) {
        // Silent — form remains usable with auth-only defaults.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Availability check dropped — the update callable does the collision
  // check server-side and returns "already-exists" if taken, which is
  // reliable and doesn't depend on any client-side Firestore read.
  const normalizedUsername = normalizeCandidate(username);
  const usernameErr = usernameValidationError(normalizedUsername);
  const canSave =
      role
      && displayName.trim().length >= 2
      && !usernameErr;

  const onSave = async () => {
    if (!canSave || !user) return;
    setErr(null); setOk(null);
    let e164 = "";
    if (phone.trim()) {
      e164 = normalizePhone(phone, cc);
      if (!PHONE_RE.test(e164)) {
        setErr("Phone number doesn't look right — leave it empty or fix it.");
        return;
      }
    }
    setBusy(true);
    try {
      // All the read-validate-rename-write logic is server-side inside
      // popMarketUpdateMyProfile — client just sends the desired values
      // and gets ok/error back. Avoids every client-Firestore SDK pitfall.
      const res = await updateMyProfile({
        displayName: displayName.trim(),
        username: normalizedUsername,
        popMarketRole: role,
        whatsapp: e164 || null,
      });
      const savedUsername = res?.data?.username || normalizedUsername;
      setAppUser({
        ...appUser,
        displayName: displayName.trim(),
        username: savedUsername,
        popMarketRole: role,
        popMarketContact: e164 ? { whatsapp: e164, phone: e164 } : null,
      });
      setOk("Saved.");
    } catch (e) {
      // Callable errors surface here as HttpsError — the .message
      // carries the human-readable reason (e.g., "That username is
      // already taken.").
      setErr(e.message || "Couldn't save that — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!appUser) {
    return (
      <div className="container" style={{ paddingTop: 32, maxWidth: 480 }}>
        <p>Loading…</p>
        <p style={{ fontSize: 12, color: "#7a7d86" }}>Stage: {stage}</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 640, paddingBottom: 60 }}>
      <style>{editCss}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>Edit profile</h1>
        {appUser.username && (
          <Link to={`/u/${appUser.username}`} className="ep-view-link">
            View public profile →
          </Link>
        )}
      </div>

      <div className="ep-field">
        <label>DISPLAY NAME</label>
        <input
          type="text" maxLength={40}
          value={displayName}
          onChange={(e) => {
            editedRef.current.displayName = true;
            setDisplayName(e.target.value);
          }}
        />
      </div>

      <div className="ep-field">
        <label>USERNAME</label>
        <div style={{ position: "relative" }}>
          <span className="ep-at">@</span>
          <input
            type="text" maxLength={20}
            value={username}
            onChange={(e) => {
              editedRef.current.username = true;
              setUsername(e.target.value);
            }}
            style={{ paddingLeft: 26 }}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
        <HintLine
          normalized={normalizedUsername}
          rawInput={username}
          err={usernameErr}
        />
      </div>

      <div className="ep-field">
        <label>ROLE</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { key: "seller", label: "💎 SELLER" },
            { key: "buyer",  label: "🎯 BUYER" },
            { key: "both",   label: "🔁 BOTH"  },
          ].map((r) => {
            const active = role === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => {
                  editedRef.current.role = true;
                  setRole(r.key);
                }}
                className="ep-role-btn"
                style={{
                  background: active ? "rgba(76,110,245,0.15)" : "rgba(255,255,255,0.03)",
                  borderColor: active ? "#4c6ef5" : "rgba(255,255,255,0.10)",
                  color: active ? "#fff" : "#c8ced5",
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="ep-field">
        <label>WHATSAPP (OPTIONAL)</label>
        <div className="ep-phone-row">
          <select value={cc} onChange={(e) => setCc(e.target.value)}>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label} {c.code}</option>
            ))}
          </select>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder="9876543210"
            value={phone}
            onChange={(e) => {
              editedRef.current.phone = true;
              setPhone(e.target.value);
            }}
          />
        </div>
      </div>

      <ContactRequestsInbox requests={pendingRequests} />

      {err && <div style={{ marginTop: 12, fontSize: 13, color: "#ff6f61" }}>⚠️ {err}</div>}
      {ok && <div style={{ marginTop: 12, fontSize: 13, color: "#4de37c" }}>✓ {ok}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 24 }}>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave || busy}
          className="ep-save"
          style={{
            background: canSave ? "#4c6ef5" : "rgba(255,255,255,0.06)",
            color: canSave ? "#fff" : "#5c6069",
            cursor: canSave && !busy ? "pointer" : "not-allowed",
          }}
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={() => signOut().then(() => navigate("/"))}
          className="ep-signout"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// Inbox of pending WhatsApp requests — surfaces on /me/edit so the
// owner can approve/deny in the same place they manage their profile.
function ContactRequestsInbox({ requests }) {
  const [busyId, setBusyId] = useState(null);
  const [rowErr, setRowErr] = useState({});
  if (!requests || requests.length === 0) return null;

  const respond = async (id, action) => {
    setBusyId(id);
    setRowErr((s) => ({ ...s, [id]: null }));
    try {
      await respondContact({ requestId: id, action });
    } catch (e) {
      setRowErr((s) => ({ ...s, [id]: e.message || String(e) }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      marginTop: 24,
      padding: 14,
      borderRadius: 12,
      background: "rgba(76,110,245,0.06)",
      border: "1px solid rgba(76,110,245,0.25)",
    }}>
      <div style={{
        fontSize: 11, letterSpacing: 2, color: "#6bb6ff",
        fontWeight: 800, marginBottom: 10,
      }}>
        💬 WHATSAPP REQUESTS ({requests.length})
      </div>
      {requests.map((r) => (
        <div key={r.id} style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 4px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexWrap: "wrap",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: "#eaeaef", wordBreak: "break-word" }}>
              <b>{r.fromDisplayName || `@${r.fromUsername || "user"}`}</b>
              {r.fromUsername && r.fromDisplayName && (
                <span style={{ color: "#9ca0a8" }}> · @{r.fromUsername}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#7a7d86" }}>
              wants your WhatsApp
            </div>
            {rowErr[r.id] && (
              <div style={{ fontSize: 11, color: "#ff6f61", marginTop: 4 }}>
                ⚠️ {rowErr[r.id]}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => respond(r.id, "approve")}
            disabled={busyId === r.id}
            style={{
              padding: "6px 12px", borderRadius: 6,
              background: "#25D366", color: "#0d1017",
              fontWeight: 800, fontSize: 11.5, letterSpacing: 0.6,
              border: "none", cursor: busyId === r.id ? "wait" : "pointer",
            }}
          >
            {busyId === r.id ? "…" : "Approve"}
          </button>
          <button
            type="button"
            onClick={() => respond(r.id, "deny")}
            disabled={busyId === r.id}
            style={{
              padding: "6px 12px", borderRadius: 6,
              background: "transparent", color: "#ff6f61",
              border: "1px solid #ff6f6144",
              fontWeight: 800, fontSize: 11.5, letterSpacing: 0.6,
              cursor: busyId === r.id ? "wait" : "pointer",
            }}
          >
            Deny
          </button>
        </div>
      ))}
    </div>
  );
}

function HintLine({ normalized, rawInput, err }) {
  const showNormalized = rawInput && normalized && normalized !== rawInput;
  return (
    <div style={{ minHeight: 18, marginTop: 6, fontSize: 12 }}>
      {showNormalized && (
        <span style={{ color: "#9ca0a8" }}>
          Will save as <b>@{normalized}</b>
        </span>
      )}
      {err && !showNormalized && (
        <span style={{ color: "#ff6f61" }}>{err}</span>
      )}
      {!err && rawInput && (
        <span style={{ color: "#9ca0a8", marginLeft: showNormalized ? 8 : 0 }}>
          Availability will be checked when you save.
        </span>
      )}
    </div>
  );
}

const editCss = `
  .ep-field { margin-bottom: 16px; }
  .ep-field label {
    display: block;
    font-size: 11px; letter-spacing: 2px;
    color: #7a7d86; font-weight: 800;
    margin-bottom: 6px;
  }
  .ep-field input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(0,0,0,0.25);
    color: #eaeaef;
    font-size: 14px;
    box-sizing: border-box;
  }
  .ep-at {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%);
    color: #7a7d86; font-size: 14px;
    pointer-events: none; font-weight: 700;
  }
  .ep-role-btn {
    padding: 10px 16px; border-radius: 8px;
    border: 1.5px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.03);
    color: #c8ced5; font-weight: 700;
    letter-spacing: 1px; font-size: 12px;
    cursor: pointer;
  }
  .ep-phone-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 8px;
  }
  .ep-phone-row select,
  .ep-phone-row input {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(0,0,0,0.25);
    color: #eaeaef; font-size: 14px;
    min-width: 0;
  }
  .ep-save {
    padding: 12px 24px; border-radius: 10px;
    border: none; font-size: 14px;
    font-weight: 900; letter-spacing: 1.4px;
    text-transform: uppercase;
  }
  .ep-signout {
    padding: 12px 20px; border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.14);
    background: transparent; color: #c8ced5;
    font-size: 12.5px; font-weight: 700;
    letter-spacing: 0.6px; cursor: pointer;
  }
  .ep-view-link {
    font-size: 12.5px; color: #6bb6ff;
    text-decoration: none;
    padding: 4px 10px; border-radius: 6px;
    background: rgba(107,182,255,0.10);
    border: 1px solid rgba(107,182,255,0.20);
  }
  @media (max-width: 500px) {
    .ep-phone-row { grid-template-columns: 90px 1fr; }
  }
`;
